/**
 * Language/Localization Helpers
 * Utilities for handling multilingual content
 */

export interface MultiLanguageName {
    english: string
    native?: string
    [key: string]: string | undefined
}

/**
 * Selects the appropriate name based on current language
 * Falls back to English if native is not available
 *
 * @example
 * ```ts
 * const productName = { english: 'Green Tea', native: 'ชาเขียว' }
 *
 * selectEnglishOrNative(productName, 'TH')  // 'ชาเขียว'
 * selectEnglishOrNative(productName, 'EN')  // 'Green Tea'
 * selectEnglishOrNative(productName, 'JP')  // 'Green Tea' (fallback)
 * ```
 *
 * @param name - Object containing language variants
 * @param language - Current language code (e.g., 'TH', 'EN', 'JP')
 * @returns The localized name or English fallback
 */
export function selectEnglishOrNative(name: MultiLanguageName, language: string): string {
    // If English, return English
    if (language.toUpperCase() === 'EN') {
        return name.english
    }

    // Try to get native name, fallback to English
    return name.native || name.english
}

/**
 * Gets a localized field from an object by language code
 *
 * @example
 * ```ts
 * const obj = {
 *   'name': 'Product',
 *   'name@th': 'สินค้า',
 *   'name@jp': '製品'
 * }
 *
 * getLocalizedField(obj, 'name', 'th')  // 'สินค้า'
 * getLocalizedField(obj, 'name', 'fr')  // 'Product' (fallback)
 * ```
 */
export function getLocalizedField<T extends Record<string, unknown>>(obj: T, fieldName: string, language: string): string {
    const localizedKey = `${fieldName}@${language.toLowerCase()}`

    if (localizedKey in obj && typeof obj[localizedKey] === 'string') {
        return obj[localizedKey] as string
    }

    if (fieldName in obj && typeof obj[fieldName] === 'string') {
        return obj[fieldName] as string
    }

    return ''
}

export const LanguageHelper = {
    selectEnglishOrNative,
    getLocalizedField
}
